import { ArrowRightIcon, InformationCircleIcon } from '@heroicons/react/20/solid'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import type {
	AdditionalQuestionsAnalyticsDataSchema,
	AdditionalQuestionsFunnelStepDataSchema,
	AdditionalQuestionsTimeToAnswerDataSchema,
} from '@jobilla/nexus-sdk'
import { motion } from 'motion/react'
import { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getLocale } from '../../../hooks/helpers'

type DateRangeOption = '7d' | '30d' | '90d' | 'all'

export type AdditionalQuestionsAnalyticsProps = {
	data: AdditionalQuestionsAnalyticsDataSchema | null
	isLoading?: boolean
	onDateRangeChange?: (startDate: string, endDate: string) => void
}

export function AdditionalQuestionsAnalytics({
	data,
	isLoading,
	onDateRangeChange,
}: AdditionalQuestionsAnalyticsProps) {
	const { t } = useTranslation('analytics')
	const [selectedRange, setSelectedRange] = useState<DateRangeOption>('30d')

	const handleDateRangeChange = (range: DateRangeOption) => {
		setSelectedRange(range)
		if (onDateRangeChange) {
			const endDate = new Date().toISOString().split('T')[0]
			let startDate: string

			switch (range) {
				case '7d':
					startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case '30d':
					startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case '90d':
					startDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
					break
				case 'all':
					startDate = '2020-01-01'
					break
			}

			onDateRangeChange(startDate, endDate)
		}
	}

	if (isLoading) {
		return (
			<div className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm">
				<div className="relative">
					<div className="mb-4">
						<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-2">
							{t('additional_questions.title')}
						</h2>
						<p className="text-sm text-grey-600">{t('additional_questions.loading')}</p>
					</div>
					<div className="space-y-4">
						{[...Array(4)].map((_, i) => (
							<div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
						))}
					</div>
				</div>
			</div>
		)
	}

	if (!data || data.funnel.length === 0) {
		return (
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ duration: 0.2 }}
				className="relative overflow-hidden p-6 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm"
			>
				<div className="flex gap-4 items-start">
					<div className="text-slate-400 flex-shrink-0 p-2 bg-slate-100 rounded-lg">
						<ChartBarIcon className="size-6" />
					</div>
					<div>
						<h2 className="text-base font-semibold text-grey-900 mb-2">
							{t('additional_questions.noDataTitle')}
						</h2>
						<p className="text-sm text-grey-600 leading-relaxed">
							{t('additional_questions.noDataDescription')}
						</p>
					</div>
				</div>
			</motion.div>
		)
	}

	const baseline = data.funnel[0]
	const submitted = data.funnel[data.funnel.length - 1]
	const revealed = data.funnel.find(s => s.step === 'revealed')

	const overallConversion = baseline.sessions > 0
		? ((submitted.sessions / baseline.sessions) * 100).toFixed(1)
		: '0'

	const revealRate = baseline.sessions > 0 && revealed
		? ((revealed.sessions / baseline.sessions) * 100).toFixed(1)
		: '0'

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2 }}
			className="relative overflow-hidden p-8 rounded-xl border border-slate-200/60 bg-gradient-to-br from-slate-50 via-white to-slate-50/30 shadow-sm"
		>
			<div className="relative">
				<div className="flex items-start justify-between mb-6">
					<div>
						<div className="flex items-center gap-2 mb-2">
							<h2 className="text-xs uppercase tracking-wide font-semibold text-grey-500">
								{t('additional_questions.title')}
							</h2>
							<span className="group relative inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 rounded cursor-help">
								{t('additional_questions.beta')}
								<InformationCircleIcon className="w-3 h-3" />
								<span className="invisible group-hover:visible absolute left-0 top-full mt-1 w-48 p-2 text-xs normal-case tracking-normal font-normal bg-grey-900 text-white rounded shadow-lg z-10">
									{t('additional_questions.betaTooltip')}
								</span>
							</span>
						</div>
						<p className="text-sm text-grey-600">
							{t('additional_questions.subtitle')}
						</p>
					</div>
					<div className="flex gap-1 bg-slate-100 rounded-lg p-1">
						{(['7d', '30d', '90d', 'all'] as DateRangeOption[]).map(range => (
							<button
								key={range}
								onClick={() => handleDateRangeChange(range)}
								className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
									selectedRange === range
										? 'bg-white text-grey-900 shadow-sm'
										: 'text-grey-600 hover:text-grey-900'
								}`}
							>
								{t(`additional_questions.dateRange.${range}`)}
							</button>
						))}
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
					<SummaryCard
						label={t('additional_questions.completedForm')}
						value={formatNumber(baseline.sessions)}
						description={t('additional_questions.completedFormDesc')}
						color="indigo"
					/>
					<SummaryCard
						label={t('additional_questions.overallConversion')}
						value={`${overallConversion}%`}
						description={t(
							'additional_questions.submittedAdditional',
							{ count: formatNumber(submitted.sessions) } as Record<string, string>,
						)}
						color={Number(overallConversion) >= 50
							? 'emerald'
							: Number(overallConversion) >= 25
							? 'amber'
							: 'red'}
					/>
					<SummaryCard
						label={t('additional_questions.revealRate')}
						value={`${revealRate}%`}
						description={t(
							'additional_questions.sawAdditionalQuestions',
							{ count: formatNumber(revealed?.sessions ?? 0) } as Record<string, string>,
						)}
						color="cyan"
					/>
				</div>

				<div className="flex items-stretch gap-4">
					{data.funnel.map((step, index) => (
						<Fragment key={step.step}>
							<FunnelStage step={step} index={index} />
							{index < data.funnel.length - 1 && (
								<FunnelArrow
									dropCount={Math.max(0, step.sessions - data.funnel[index + 1].sessions)}
									index={index}
								/>
							)}
						</Fragment>
					))}
				</div>

				{data.timeToAnswer.length > 0 && data.timeToAnswer.some(q => q.answerCount > 0) && (
					<div className="mt-8">
						<div className="text-xs uppercase tracking-wide font-semibold text-grey-500 mb-3">
							{t('additional_questions.responseTimes')}
						</div>
						<div className="space-y-2">
							{data.timeToAnswer.map(question => (
								<TimeToAnswerRow key={question.questionId} question={question} />
							))}
						</div>
					</div>
				)}
			</div>
		</motion.div>
	)
}

const stageColors: Record<string, { color: string; bgColor: string; borderColor: string }> = {
	completed_form: { color: 'text-indigo-700', bgColor: 'bg-indigo-50', borderColor: 'border-indigo-200' },
	revealed: { color: 'text-cyan-700', bgColor: 'bg-cyan-50', borderColor: 'border-cyan-200' },
	answered_one: { color: 'text-orange-700', bgColor: 'bg-orange-50', borderColor: 'border-orange-200' },
	submitted: { color: 'text-emerald-700', bgColor: 'bg-emerald-50', borderColor: 'border-emerald-200' },
}

function FunnelStage({ step, index }: { step: AdditionalQuestionsFunnelStepDataSchema; index: number }) {
	const { t } = useTranslation('analytics')
	const colors = stageColors[step.step] ?? stageColors.completed_form

	return (
		<motion.div
			initial={{ opacity: 0, scale: 0.9 }}
			animate={{ opacity: 1, scale: 1 }}
			transition={{ duration: 0.3, delay: 0.2 + index * 0.1 }}
			className="relative flex-1 h-[200px]"
		>
			<div
				className={`relative ${colors.bgColor} ${colors.borderColor} border-2 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-md h-full flex items-center justify-center`}
			>
				<div className="p-6 w-full">
					<div className="text-center">
						<h3 className={`text-base font-bold ${colors.color} mb-2`}>
							{step.label}
						</h3>
						<div className={`text-3xl font-bold ${colors.color} mb-2`}>
							{formatNumber(step.sessions)}
						</div>
						<p className="text-xs text-grey-600 mb-3">{t('additional_questions.sessions')}</p>
						{step.conversionRate !== null && (
							<div className="text-xs font-semibold text-grey-600 bg-white/80 px-2 py-1 rounded inline-block">
								{t('additional_questions.fromPrev', { rate: step.conversionRate })}
							</div>
						)}
					</div>
				</div>
			</div>
		</motion.div>
	)
}

function FunnelArrow({ dropCount, index }: { dropCount: number; index: number }) {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
			className="flex flex-col items-center justify-center gap-1 flex-shrink-0 px-2"
		>
			<ArrowRightIcon className="w-8 h-8 text-grey-400" />
			<div className="text-xs font-semibold text-grey-600 bg-white px-2 py-1 rounded-full shadow-sm border border-slate-200 whitespace-nowrap">
				-{formatNumber(dropCount)}
			</div>
		</motion.div>
	)
}

function SummaryCard({
	label,
	value,
	description,
	color,
}: {
	label: string
	value: string
	description: string
	color: 'indigo' | 'emerald' | 'amber' | 'red' | 'cyan'
}) {
	const colorClasses = {
		indigo: {
			bg: 'bg-indigo-50/70',
			border: 'border-indigo-200',
			text: 'text-indigo-700',
			value: 'text-indigo-900',
		},
		emerald: {
			bg: 'bg-emerald-50/70',
			border: 'border-emerald-200',
			text: 'text-emerald-700',
			value: 'text-emerald-900',
		},
		amber: { bg: 'bg-amber-50/70', border: 'border-amber-200', text: 'text-amber-700', value: 'text-amber-900' },
		red: { bg: 'bg-red-50/70', border: 'border-red-200', text: 'text-red-700', value: 'text-red-900' },
		cyan: { bg: 'bg-cyan-50/70', border: 'border-cyan-200', text: 'text-cyan-700', value: 'text-cyan-900' },
	}

	const classes = colorClasses[color]

	return (
		<div className={`p-4 rounded-lg ${classes.bg} border-2 ${classes.border}`}>
			<div className={`text-xs font-semibold ${classes.text} uppercase tracking-wide mb-1`}>{label}</div>
			<div className={`text-2xl font-bold ${classes.value} mb-1`}>{value}</div>
			<div className="text-xs text-grey-600">{description}</div>
		</div>
	)
}

function TimeToAnswerRow({ question }: { question: AdditionalQuestionsTimeToAnswerDataSchema }) {
	const { t } = useTranslation('analytics')

	if (question.answerCount === 0) {
		return null
	}

	const avgSeconds = question.avgTimeToAnswerMs !== null
		? (question.avgTimeToAnswerMs / 1000).toFixed(1)
		: null

	return (
		<div className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-300 transition-all">
			<div className="flex items-center gap-3">
				<div className="flex-shrink-0 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-grey-700">
					{question.position + 1}
				</div>
				<div>
					<div className="text-sm font-medium text-grey-900">
						{truncateLabel(question.label, 60)}
					</div>
					<div className="text-xs text-grey-500 capitalize">{question.type}</div>
				</div>
			</div>
			<div className="flex items-center gap-4 text-xs text-grey-600">
				{avgSeconds !== null && (
					<span>
						<span className="font-semibold text-grey-900">{avgSeconds}s</span>{' '}
						{t('additional_questions.avg')}
					</span>
				)}
				<span>
					<span className="font-semibold text-grey-900">{formatNumber(question.answerCount)}</span>{' '}
					{question.answerCount === 1 ? t('additional_questions.answer') : t('additional_questions.answers')}
				</span>
			</div>
		</div>
	)
}

function formatNumber(num: number): string {
	return new Intl.NumberFormat(getLocale()).format(num)
}

function truncateLabel(label: string, maxLength: number): string {
	if (label.length <= maxLength) { return label }
	return label.slice(0, maxLength - 3) + '...'
}
